import { toast } from "sonner";
import { Bookmark, Plus, X } from "lucide-react";
import { apiError } from "@/lib/api";
import { UsualExtras, extraFromPreset, saveAsUsual } from "./UsualExtras";

const num = (v) => Number(v) || 0;
// Extras and VAT can carry pence, so these amounts always show two decimals.
export const gbp2 = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 }).format(num(n));
export const extrasTotal = (extras) => (extras || []).reduce((t, e) => t + num(e.value) + num(e.vat), 0);
export const BLANK_EXTRA = { description: "", value: "", vat: "", funds_received: false };
// Form-state copy of a saved extra (numbers as editable strings).
export const extraForForm = (e) => ({ description: e.description, value: e.value || "", vat: e.vat || "", funds_received: !!e.funds_received });

const ExtraRow = ({ e, i, onChange, onRemove, onSave }) => {
  const set = (k) => (ev) => onChange({ ...e, [k]: ev.target.value });
  return (
    <div className="grid gap-3 border border-stone p-4 sm:grid-cols-[1fr_8rem_8rem_auto]" data-testid={`sale-extra-${i}`}>
      <div>
        <label className="block text-xs font-semibold uppercase tracking-[0.1em] text-charcoal/75">Description
          <textarea rows={2} value={e.description} onChange={set("description")} className="field mt-1.5 text-sm normal-case tracking-normal" placeholder="e.g. Warranty Wise 12-month warranty, delivery, ceramic coating" data-testid={`sale-extra-${i}-description`} />
        </label>
        <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-charcoal">
          <input type="checkbox" checked={!!e.funds_received} onChange={(ev) => onChange({ ...e, funds_received: ev.target.checked })} data-testid={`sale-extra-${i}-funds-received`} />
          Cleared funds received before work began
        </label>
      </div>
      <label className="text-xs font-semibold uppercase tracking-[0.1em] text-charcoal/75">Value (£)
        <input type="number" min="0" step="0.01" value={e.value} onChange={set("value")} className="field mt-1.5 text-sm" data-testid={`sale-extra-${i}-value`} />
      </label>
      <label className="text-xs font-semibold uppercase tracking-[0.1em] text-charcoal/75">VAT (£)
        <input type="number" min="0" step="0.01" value={e.vat} onChange={set("vat")} className="field mt-1.5 text-sm" data-testid={`sale-extra-${i}-vat`} />
        <button type="button" onClick={() => onChange({ ...e, vat: (num(e.value) * 0.2).toFixed(2) })} className="mt-1 text-xs normal-case tracking-normal text-forest underline" data-testid={`sale-extra-${i}-vat20`}>Add 20%</button>
      </label>
      <div className="flex items-start justify-between gap-3 sm:flex-col sm:items-end">
        <p className="pt-6 text-sm font-semibold" data-testid={`sale-extra-${i}-total`}>{gbp2(num(e.value) + num(e.vat))}</p>
        <div className="flex items-center gap-1">
          <button type="button" onClick={onSave} title="Save as usual extra" aria-label={`Save extra ${i + 1} as a usual extra`} className="inline-flex items-center gap-1 p-1 text-xs text-forest hover:bg-stone-light" data-testid={`sale-extra-${i}-save-usual`}><Bookmark size={14} /> Save as usual extra</button>
          <button type="button" onClick={onRemove} aria-label={`Remove extra ${i + 1}`} className="p-1 text-red-800 hover:bg-red-50" data-testid={`sale-extra-${i}-remove`}><X size={16} /></button>
        </div>
      </div>
    </div>
  );
};

export function SaleExtras({ extras, onChange, presets, setPresets }) {
  const update = (i, e) => onChange(extras.map((x, j) => (j === i ? e : x)));
  const save = async (e) => {
    try {
      setPresets(await saveAsUsual(e, presets));
      toast.success(`"${e.description.trim()}" saved to your usual extras`);
    } catch (err) {
      toast.error(err.response ? apiError(err) : err.message);
    }
  };
  return (
    <fieldset className="mb-6" data-testid="sale-extras">
      <legend className="mb-3 text-sm font-semibold text-forest">Extras (added to the total price)</legend>
      <UsualExtras presets={presets} setPresets={setPresets} onPick={(p) => onChange([...extras, extraFromPreset(p)])} />
      <div className="space-y-3">
        {extras.map((e, i) => <ExtraRow key={i} e={e} i={i} onChange={(x) => update(i, x)} onSave={() => save(e)} onRemove={() => onChange(extras.filter((_, j) => j !== i))} />)}
      </div>
      <button type="button" onClick={() => onChange([...extras, BLANK_EXTRA])} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-forest" data-testid="sale-extra-add"><Plus size={16} /> Add an extra</button>
    </fieldset>
  );
}
